import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useState } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    inquiryType: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Animation refs
  const heroRef = useScrollAnimation();
  const formRef = useScrollAnimation();
  const contactInfoRef = useScrollAnimation({ delay: 100 });
  const marketsHeadingRef = useScrollAnimation();
  const marketCard1Ref = useScrollAnimation({ delay: 0 });
  const marketCard2Ref = useScrollAnimation({ delay: 50 });
  const marketCard3Ref = useScrollAnimation({ delay: 100 });
  const marketCard4Ref = useScrollAnimation({ delay: 150 });
  const marketCard5Ref = useScrollAnimation({ delay: 200 });
  const marketCard6Ref = useScrollAnimation({ delay: 250 });
  const marketCard7Ref = useScrollAnimation({ delay: 300 });
  const marketCard8Ref = useScrollAnimation({ delay: 350 });
  const marketCard9Ref = useScrollAnimation({ delay: 400 });
  const marketCard10Ref = useScrollAnimation({ delay: 450 });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('inquiryType', formData.inquiryType);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6q61a6a739gopmlh51g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', inquiryType: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const marketRefs = [
    marketCard1Ref,
    marketCard2Ref,
    marketCard3Ref,
    marketCard4Ref,
    marketCard5Ref,
    marketCard6Ref,
    marketCard7Ref,
    marketCard8Ref,
    marketCard9Ref,
    marketCard10Ref,
  ];

  return (
    <div className="min-h-screen bg-[#F5F5F3]">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center bg-[#1F2A44] mt-20">
        <div className="absolute inset-0 opacity-10">
          <div className="w-full h-full" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.05) 10px, rgba(255,255,255,.05) 20px)' }}></div>
        </div>
        <div ref={heroRef} className="relative max-w-[1440px] mx-auto px-20 w-full text-center">
          <h1
            className="text-[52px] text-white leading-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
          >
            Contact Us
          </h1>
          <p
            className="text-[17px] text-white/85 max-w-[680px] mx-auto"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            We welcome inquiries from investors, partners, and community stakeholders
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="bg-white py-36">
        <div className="max-w-[1440px] mx-auto px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <div ref={formRef}>
              <h2
                className="text-[32px] text-[#1F2A44] mb-8"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
              >
                Send Us a Message
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="contact-form">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-[14px] text-[#1F2A44] font-medium mb-2"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full h-13 px-4 border border-[#D9D9D6] rounded text-[16px] focus:outline-none focus:border-[#B89C4B] transition-colors"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-[14px] text-[#1F2A44] font-medium mb-2"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full h-13 px-4 border border-[#D9D9D6] rounded text-[16px] focus:outline-none focus:border-[#B89C4B] transition-colors"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  />
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-[14px] text-[#1F2A44] font-medium mb-2"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full h-13 px-4 border border-[#D9D9D6] rounded text-[16px] focus:outline-none focus:border-[#B89C4B] transition-colors"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  />
                </div>

                <div>
                  <label
                    htmlFor="inquiryType"
                    className="block text-[14px] text-[#1F2A44] font-medium mb-2"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Inquiry Type *
                  </label>
                  <select
                    id="inquiryType"
                    name="inquiryType"
                    required
                    value={formData.inquiryType}
                    onChange={(e) => setFormData({ ...formData, inquiryType: e.target.value })}
                    className="w-full h-13 px-4 border border-[#D9D9D6] rounded text-[16px] focus:outline-none focus:border-[#B89C4B] transition-colors cursor-pointer"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    <option value="">Select Type</option>
                    <option value="investor">Investor Inquiry</option>
                    <option value="partnership">Partnership Opportunity</option>
                    <option value="general">General Question</option>
                    <option value="media">Media Inquiry</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-[14px] text-[#1F2A44] font-medium mb-2"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    required
                    maxLength={500}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-3 border border-[#D9D9D6] rounded text-[16px] focus:outline-none focus:border-[#B89C4B] transition-colors resize-none"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  ></textarea>
                  <p className="text-[13px] text-[#2E2E2E]/60 mt-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {formData.message.length}/500 characters
                  </p>
                </div>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded">
                    <p className="text-[15px] text-green-800" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Thank you for your message. We will respond shortly.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded">
                    <p className="text-[15px] text-red-800" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      There was an error sending your message. Please try again.
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-14 bg-[#B89C4B] text-[#1F2A44] text-[15px] font-medium rounded transition-all duration-300 hover:bg-[#1F2A44] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div ref={contactInfoRef} className="space-y-10">
              <div>
                <h3
                  className="text-[18px] text-[#1F2A44] font-semibold mb-1"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  General Inquiries
                </h3>
                <div className="w-10 h-0.5 bg-[#B89C4B] mb-5"></div>
                <div className="space-y-3">
                  <p
                    className="text-[16px] text-[#2E2E2E]"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    <i className="ri-mail-line text-[#B89C4B] mr-3"></i>
                    <a href="mailto:Andrew.Joseph@martellInvestmentCapital.com" className="hover:text-[#B89C4B] transition-colors cursor-pointer">
                      Andrew.Joseph@martellInvestmentCapital.com
                    </a>
                  </p>
                  <p
                    className="text-[16px] text-[#2E2E2E]"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    <i className="ri-phone-line text-[#B89C4B] mr-3"></i>
                    <a href="tel:+12489435541" className="hover:text-[#B89C4B] transition-colors cursor-pointer">
                      1-248-943-5541
                    </a>
                  </p>
                </div>
              </div>

              <div>
                <h3
                  className="text-[18px] text-[#1F2A44] font-semibold mb-1"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Investor Relations
                </h3>
                <div className="w-10 h-0.5 bg-[#B89C4B] mb-5"></div>
                <p
                  className="text-[16px] text-[#2E2E2E]"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-mail-line text-[#B89C4B] mr-3"></i>
                  <a href="mailto:Andrew.Joseph@martellInvestmentCapital.com" className="hover:text-[#B89C4B] transition-colors cursor-pointer">
                    Andrew.Joseph@martellInvestmentCapital.com
                  </a>
                </p>
              </div>

              <div>
                <h3
                  className="text-[18px] text-[#1F2A44] font-semibold mb-1"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Service Area
                </h3>
                <div className="w-10 h-0.5 bg-[#B89C4B] mb-5"></div>
                <p
                  className="text-[16px] text-[#2E2E2E] mb-3 font-medium"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Midwest Markets
                </p>
                <ul className="space-y-2">
                  {['Indiana', 'Ohio', 'Michigan', 'Illinois', 'Wisconsin'].map((state, index) => (
                    <li
                      key={index}
                      className="text-[15px] text-[#2E2E2E]"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      <i className="ri-map-pin-line text-[#B89C4B] mr-3"></i>
                      {state}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-[#F5F5F3] p-8 rounded-lg">
                <h3
                  className="text-[18px] text-[#1F2A44] font-semibold mb-4"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Office Hours
                </h3>
                <div className="space-y-2">
                  <p
                    className="text-[15px] text-[#2E2E2E]"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Monday - Friday: 9:00 AM - 5:00 PM
                  </p>
                  <p
                    className="text-[15px] text-[#2E2E2E]"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Saturday - Sunday: Closed
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="bg-[#F5F5F3] py-28">
        <div className="max-w-[1440px] mx-auto px-20">
          <h2
            ref={marketsHeadingRef}
            className="text-[38px] text-[#1F2A44] text-center mb-12"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
          >
            Our Markets
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
            {[
              'Indianapolis, IN',
              'Columbus, OH',
              'Grand Rapids, MI',
              'Fort Wayne, IN',
              'Dayton, OH',
              'South Bend, IN',
              'Toledo, OH',
              'Lansing, MI',
              'Evansville, IN',
              'Akron, OH',
            ].map((city, index) => (
              <div
                key={index}
                ref={marketRefs[index]}
                className="bg-white p-5 rounded-lg text-center border-2 border-transparent hover:border-[#B89C4B] transition-all duration-300"
              >
                <i className="ri-map-pin-2-fill text-3xl text-[#B89C4B] mb-3"></i>
                <p
                  className="text-[15px] text-[#1F2A44] font-medium"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {city}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;